import React, { useState, useEffect, useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  FolderArrowDownIcon,
  UserIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { MatterDetailed } from '../../../types/matters';

interface CloseMatterModalProps {
  isOpen: boolean;
  onClose: () => void;
  matter: MatterDetailed;
  onArchive: (data: ArchiveData) => Promise<void>;
  onTransfer: (data: TransferData) => Promise<void>;
}

interface ArchiveData {
  archive_reason: string;
  archive_notes: string;
  keep_visible: boolean; // Whether to keep matter visible in main table
}

interface TransferData {
  to_attorney_id: string;
  transfer_reason: string;
  transfer_notes: string;
  client_notified: boolean;
}

interface Attorney {
  user_id: string;
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role?: string;
}

const ARCHIVE_REASONS = [
  { value: 'case_settled', label: 'Case Settled' },
  { value: 'case_completed', label: 'Case Completed' },
  { value: 'case_dismissed', label: 'Case Dismissed' },
  { value: 'client_requested', label: 'Client Requested' },
  { value: 'transferred_counsel', label: 'Transferred to Other Counsel' },
  { value: 'other', label: 'Other' }
];

const TRANSFER_REASONS = [
  { value: 'workload_redistribution', label: 'Workload Redistribution' },
  { value: 'specialty_expertise', label: 'Specialty Expertise Required' },
  { value: 'client_request', label: 'Client Request' },
  { value: 'attorney_departure', label: 'Professional Departure' },
  { value: 'conflict_resolution', label: 'Conflict Resolution' },
  { value: 'other', label: 'Other' }
];

const CloseMatterModal: React.FC<CloseMatterModalProps> = ({
  isOpen,
  onClose,
  matter,
  onArchive,
  onTransfer
}) => {
  const { keycloak } = useKeycloak();
  const [selectedOption, setSelectedOption] = useState<'archive' | 'transfer' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attorneys, setAttorneys] = useState<Attorney[]>([]);
  const [loadingAttorneys, setLoadingAttorneys] = useState(false);
  
  // Archive form state
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveNotes, setArchiveNotes] = useState('');
  const [keepVisible, setKeepVisible] = useState(true); // Default to keeping visible
  
  // Transfer form state
  const [selectedAttorney, setSelectedAttorney] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  const [clientNotified, setClientNotified] = useState(false);
  const [showAssignConfirmation, setShowAssignConfirmation] = useState(false);

  // Get JWT token from Keycloak (following working pattern)
  const getAuthToken = useCallback(() => {
    console.log('🔑 [CloseMatterModal] Getting token from React Keycloak provider...');
    
    // 1. First try the React Keycloak provider (most reliable)
    if (keycloak?.token) {
      console.log('✅ [CloseMatterModal] Found token via React Keycloak provider');
      return keycloak.token;
    }
    
    // 2. Fall back to storage-based detection (same pattern as other working components)
    const jwtPattern = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
    
    console.log('🔍 [CloseMatterModal] Falling back to storage detection...');
    for (const storage of [sessionStorage, localStorage]) {
      for (const key of Object.keys(storage)) {
        const value = storage.getItem(key);
        if (!value) continue;
        
        if (jwtPattern.test(value)) {
          console.log(`✅ [CloseMatterModal] Found JWT token in ${storage === localStorage ? 'localStorage' : 'sessionStorage'}: ${key}`);
          return value;
        }
        
        if (value.startsWith('{')) {
          try {
            const parsed = JSON.parse(value);
            const tokenFields = ['token', 'access_token', 'accessToken', 'idToken', 'id_token'];
            for (const field of tokenFields) {
              if (parsed[field] && jwtPattern.test(parsed[field])) {
                console.log(`✅ [CloseMatterModal] Found JWT token in ${storage === localStorage ? 'localStorage' : 'sessionStorage'} ${key}.${field}`);
                return parsed[field];
              }
            }
          } catch (e) { /* ignore */ }
        }
      }
    }
    
    console.log('❌ [CloseMatterModal] No JWT token found');
    console.log('🔍 [CloseMatterModal] Keycloak authenticated:', keycloak?.authenticated);
    console.log('🔍 [CloseMatterModal] Keycloak instance:', !!keycloak);
    
    return '';
  }, [keycloak]);

  // Create headers with JWT authentication
  const getAuthHeaders = useCallback(() => {
    const token = getAuthToken();
    
    if (!token) {
      console.warn('🔒 [CloseMatterModal] No JWT token available for authentication');
    }
    
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }, [getAuthToken]);

  const loadAttorneys = useCallback(async () => {
    setLoadingAttorneys(true);
    try {
      const headers = getAuthHeaders();
      console.log('🔍 [CloseMatterModal] Loading attorneys with headers:', { ...headers, Authorization: headers.Authorization ? 'Bearer [REDACTED]' : 'Missing' });
      
      // Try working endpoints for attorneys
      const endpoints = [
        '/api/v1/users/attorneys',
        '/api/v1/admin/users/attorneys',
        '/api/v1/admin/users'
      ];

      let attorneyList: Attorney[] = [];
      
      for (const endpoint of endpoints) {
        try {
          console.log(`🔍 [CloseMatterModal] Trying endpoint: ${endpoint}`);
          
          const response = await fetch(endpoint, {
            headers
          });
          
          if (response.ok) {
            const data = await response.json();
            const users = data.data || data.users || data || [];
            console.log(`✅ [CloseMatterModal] Got ${Array.isArray(users) ? users.length : 0} users from ${endpoint}`);
            console.log(`🔍 [CloseMatterModal] Sample user data structure:`, users[0]);
            
            // For /users/attorneys endpoint, all returned users are attorneys
            if (endpoint.includes('/attorneys')) {
              attorneyList = users.map((user: any) => {
                // Extract name from various possible formats
                let firstName = 'Unknown';
                let lastName = 'User';
                
                if (user.first_name && user.last_name) {
                  firstName = user.first_name;
                  lastName = user.last_name;
                } else if (user.firstName && user.lastName) {
                  firstName = user.firstName;
                  lastName = user.lastName;
                } else if (user.name) {
                  // Split full name if available
                  const nameParts = user.name.split(' ');
                  firstName = nameParts[0] || 'Unknown';
                  lastName = nameParts.slice(1).join(' ') || 'User';
                } else if (user.username) {
                  // Use username as fallback
                  firstName = user.username;
                  lastName = '';
                } else if (user.email) {
                  // Extract name from email as last resort
                  const emailName = user.email.split('@')[0];
                  firstName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
                  lastName = '';
                }
                
                return {
                  user_id: user.user_id || user.id || user.keycloak_user_id,
                  id: user.id || user.user_id || user.keycloak_user_id,
                  first_name: firstName,
                  last_name: lastName,
                  email: user.email || 'no-email@example.com',
                  role: user.role || 'attorney'
                };
              });
            } else {
              // Filter for attorneys from general user endpoints
              attorneyList = users
                .filter((user: any) => 
                  user.role === 'attorney' || 
                  user.email?.includes('attorney') || 
                  user.email?.includes('testuser') ||
                  user.first_name?.includes('Transfer') ||
                  user.groups?.includes('Attorneys') ||
                  (user.is_active !== false && user.user_id)
                )
                .map((user: any) => ({
                  user_id: user.user_id || user.id || user.keycloak_user_id,
                  id: user.id || user.user_id || user.keycloak_user_id,
                  first_name: user.first_name || user.firstName || 'Unknown',
                  last_name: user.last_name || user.lastName || 'User',
                  email: user.email || 'no-email@example.com',
                  role: user.role || 'attorney'
                }));
            }
            
            console.log(`🔍 [CloseMatterModal] Processed ${attorneyList.length} attorneys from ${endpoint}`);
            
            if (attorneyList.length > 0) break;
          } else {
            console.warn(`❌ [CloseMatterModal] ${endpoint} returned ${response.status}`);
          }
        } catch (error) {
          console.warn(`Failed to load from ${endpoint}:`, error);
        }
      }
      
      console.log(`✅ [CloseMatterModal] Final attorney list:`, attorneyList);
      setAttorneys(attorneyList);
    } catch (error) {
      console.error('Failed to load attorneys:', error);
    } finally {
      setLoadingAttorneys(false);
    }
  }, [getAuthHeaders]);

  // Load attorneys when transfer option is selected
  useEffect(() => {
    if (selectedOption === 'transfer' && attorneys.length === 0 && keycloak?.authenticated) {
      loadAttorneys();
    }
  }, [selectedOption, keycloak?.authenticated, loadAttorneys]);

  const resetForm = () => {
    setSelectedOption(null);
    setArchiveReason('');
    setArchiveNotes('');
    setKeepVisible(true);
    setSelectedAttorney('');
    setTransferReason('');
    setTransferNotes('');
    setClientNotified(false);
    setShowAssignConfirmation(false);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleArchiveSubmit = async () => {
    if (!archiveReason) return;
    
    setIsSubmitting(true);
    try {
      await onArchive({
        archive_reason: archiveReason,
        archive_notes: archiveNotes,
        keep_visible: keepVisible
      });
      handleClose();
    } catch (error) {
      console.error('Archive failed:', error);
      setIsSubmitting(false);
    }
  };

  const handleTransferSubmit = async () => {
    console.log('🔍 [CloseMatterModal] Transfer submit clicked');
    console.log('🔍 [CloseMatterModal] Transfer form data:', {
      selectedAttorney,
      transferReason,
      transferNotes,
      clientNotified
    });
    
    if (!selectedAttorney || !transferReason) {
      console.error('❌ [CloseMatterModal] Missing required fields:', { selectedAttorney, transferReason });
      return;
    }

    // Check if trying to transfer to the same attorney
    if (matter.assigned_attorney_id && matter.assigned_attorney_id === selectedAttorney) {
      alert('Cannot transfer engagement to the same professional it is already assigned to. Please select a different professional.');
      return;
    }

    // Check if matter is unassigned and show confirmation
    if (!matter.assigned_attorney_id) {
      console.log('🔍 [CloseMatterModal] Matter is unassigned, showing confirmation');
      setShowAssignConfirmation(true);
      return;
    }
    
    console.log('🔍 [CloseMatterModal] Matter is assigned, proceeding with transfer');
    console.log('🔍 [CloseMatterModal] Current matter.assigned_attorney_id:', matter.assigned_attorney_id);
    
    // Proceed with transfer for assigned matters
    await performTransfer();
  };

  const performTransfer = async () => {
    setIsSubmitting(true);
    try {
      console.log('🔍 [CloseMatterModal] Calling onTransfer...');
      await onTransfer({
        to_attorney_id: selectedAttorney,
        transfer_reason: transferReason,
        transfer_notes: transferNotes,
        client_notified: clientNotified
      });
      console.log('✅ [CloseMatterModal] Transfer completed successfully');
      handleClose();
    } catch (error) {
      console.error('❌ [CloseMatterModal] Transfer failed:', error);
      setIsSubmitting(false);
    }
  };

  const handleConfirmAssignment = async () => {
    setShowAssignConfirmation(false);
    await performTransfer();
  };

  const canArchive = archiveReason.trim() !== '';
  const canTransfer = selectedAttorney !== '' && transferReason !== '';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    Close Engagement
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {matter.displayNumber} - {matter.title}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Option Selection */}
            {!selectedOption && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  How would you like to close this engagement?
                </p>
                
                <div className="space-y-3">
                  {/* Archive Option */}
                  <button
                    onClick={() => setSelectedOption('archive')}
                    className="w-full p-4 text-left border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <FolderArrowDownIcon className="h-8 w-8 text-blue-500 mr-3" />
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                            Archive Engagement
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Mark as completed and move to archived engagements
                          </p>
                        </div>
                      </div>
                      <ArrowRightIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
                    </div>
                  </button>

                  {/* Transfer Option */}
                  <button
                    onClick={() => setSelectedOption('transfer')}
                    className="w-full p-4 text-left border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <UserIcon className="h-8 w-8 text-green-500 mr-3" />
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                            Transfer Engagement
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Transfer to another professional
                          </p>
                        </div>
                      </div>
                      <ArrowRightIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Archive Form */}
            {selectedOption === 'archive' && (
              <div className="space-y-4">
                <div className="flex items-center mb-4">
                  <button
                    onClick={() => setSelectedOption(null)}
                    className="mr-3 text-gray-400 hover:text-gray-600"
                  >
                    ←
                  </button>
                  <FolderArrowDownIcon className="h-6 w-6 text-blue-500 mr-2" />
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                    Archive Engagement
                  </h4>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Archive Reason <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={archiveReason}
                    onChange={(e) => setArchiveReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select a reason...</option>
                    {ARCHIVE_REASONS.map(reason => (
                      <option key={reason.value} value={reason.value}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Archive Notes
                  </label>
                  <textarea
                    value={archiveNotes}
                    onChange={(e) => setArchiveNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Additional notes about archiving this engagement..."
                  />
                </div>

                {/* Engagement Visibility Option */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md p-4">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-3">
                        Show Closed Engagements?
                      </h4>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
                        Would you like this closed engagement to remain visible in your main engagements list?
                      </p>
                      
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="visibility"
                            checked={keepVisible === true}
                            onChange={() => setKeepVisible(true)}
                            className="form-radio h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
                          />
                          <span className="ml-2 text-sm text-blue-900 dark:text-blue-100">
                            <strong>Yes</strong> - Keep visible in engagements list (shows as "Closed" status)
                          </span>
                        </label>
                        
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="visibility"
                            checked={keepVisible === false}
                            onChange={() => setKeepVisible(false)}
                            className="form-radio h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
                          />
                          <span className="ml-2 text-sm text-blue-900 dark:text-blue-100">
                            <strong>No</strong> - Hide from engagements list (only visible in Subscriber Settings)
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md p-3">
                  <div className="flex">
                    <ClockIcon className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
                    <div className="text-sm text-yellow-700 dark:text-yellow-300">
                      <p className="font-medium">Archive Impact:</p>
                      <ul className="mt-1 ml-2 list-disc text-xs">
                        <li>Engagement will be moved to archived engagements in Subscriber Settings</li>
                        {keepVisible ? (
                          <li>Engagement will remain visible in main engagements list with "Closed" status</li>
                        ) : (
                          <li>Engagement will be hidden from main engagements list</li>
                        )}
                        <li>Can be restored from Subscriber Settings if needed</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Transfer Form */}
            {selectedOption === 'transfer' && (
              <div className="space-y-4">
                <div className="flex items-center mb-4">
                  <button
                    onClick={() => setSelectedOption(null)}
                    className="mr-3 text-gray-400 hover:text-gray-600"
                  >
                    ←
                  </button>
                  <UserIcon className="h-6 w-6 text-green-500 mr-2" />
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                    {matter.assigned_attorney_id ? 'Transfer Engagement' : 'Assign Professional'}
                  </h4>
                </div>

                {/* Show notice for unassigned engagements */}
                {!matter.assigned_attorney_id && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md p-3">
                    <div className="flex">
                      <ExclamationTriangleIcon className="h-5 w-5 text-blue-500 mr-2 mt-0.5" />
                      <div className="text-sm text-blue-700 dark:text-blue-300">
                        <p className="font-medium">Engagement Assignment</p>
                        <p className="mt-1">This engagement is currently unassigned. You'll be assigning it to a professional for the first time.</p>
                      </div>
                    </div>
                  </div>
                )}

                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {matter.assigned_attorney_id ? 'Transfer To' : 'Assign To'} <span className="text-red-500">*</span>
                  </label>
                  {loadingAttorneys ? (
                    <div className="flex items-center py-2 text-sm text-gray-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      Loading professionals...
                    </div>
                  ) : (
                    <select
                      value={selectedAttorney}
                      onChange={(e) => setSelectedAttorney(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Select a professional...</option>
                      {attorneys.map(attorney => (
                        <option key={attorney.user_id || attorney.id} value={attorney.user_id || attorney.id}>
                          {attorney.first_name} {attorney.last_name} ({attorney.email})
                        </option>
                      ))}
                    </select>
                  )}
                  {attorneys.length === 0 && !loadingAttorneys && (
                    <p className="text-xs text-gray-500 mt-1">
                      No professionals found. You may need to create professional users first.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Transfer Reason <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={transferReason}
                    onChange={(e) => setTransferReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select a reason...</option>
                    {TRANSFER_REASONS.map(reason => (
                      <option key={reason.value} value={reason.value}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Transfer Notes
                  </label>
                  <textarea
                    value={transferNotes}
                    onChange={(e) => setTransferNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Additional notes about this transfer..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    id="client-notified"
                    type="checkbox"
                    checked={clientNotified}
                    onChange={(e) => setClientNotified(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="client-notified" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Client has been notified of this transfer
                  </label>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md p-3">
                  <div className="flex">
                    <UserIcon className="h-5 w-5 text-blue-400 mr-2 mt-0.5" />
                    <div className="text-sm text-blue-700 dark:text-blue-300">
                      <p className="font-medium">Transfer Impact:</p>
                      <ul className="mt-1 ml-2 list-disc text-xs">
                        <li>Engagement will be assigned to the selected professional</li>
                        <li>Transfer history will be recorded</li>
                        <li>Original professional assignment will be preserved</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {selectedOption && (
            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                onClick={selectedOption === 'archive' ? handleArchiveSubmit : handleTransferSubmit}
                disabled={isSubmitting || (selectedOption === 'archive' ? !canArchive : !canTransfer)}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {selectedOption === 'archive' ? 'Archiving...' : 'Transferring...'}
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    {selectedOption === 'archive' ? 'Archive Engagement' : 'Transfer Engagement'}
                  </>
                )}
              </button>
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Assignment Confirmation Modal */}
      {showAssignConfirmation && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowAssignConfirmation(false)}
            />

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900 sm:mx-0 sm:h-10 sm:w-10">
                    <UserIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      Assign Professional to Engagement
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        This engagement is currently <strong>unassigned</strong>. Would you like to assign it to{' '}
                        <strong>
                          {attorneys.find(a => (a.user_id || a.id) === selectedAttorney)?.first_name}{' '}
                          {attorneys.find(a => (a.user_id || a.id) === selectedAttorney)?.last_name}
                        </strong>?
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                        This will be recorded as an initial assignment, not a transfer.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleConfirmAssignment}
                  disabled={isSubmitting}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Assigning...
                    </>
                  ) : (
                    'Assign Professional'
                  )}
                </button>
                <button
                  onClick={() => setShowAssignConfirmation(false)}
                  disabled={isSubmitting}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CloseMatterModal;
